import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  InputBase,
  Button,
  Pagination,
  Tooltip,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { formatPhoneNumber } from "../utils/formatnumber";
import { formatTimestamp, formatTime, formatTimestampForZone, formatTimestampForZone2, formatTimestampUtc } from "../utils/formattimestamp";
import { Search } from "@mui/icons-material";
import ExportToCSVButton from "../reusables/ExportToCsvButton";
import { ArrowRight, ArrowUpRight, Phone, Play, Volume2, MailIcon, Settings2, Download } from "lucide-react";
import { VoicemailOutlined, ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { getActionDetailsLabel } from "../utils/actionDetailsLabel";
import { Link } from "react-router-dom";

// ElevenLabs Icon Component with fallback
const ElevenLabsIcon = ({ className = "w-4 h-4" }) => {
  const [imgError, setImgError] = useState(false);
  
  if (imgError) {
    return (
      <div className={`${className} bg-indigo-500 rounded flex items-center justify-center text-white text-xs font-bold`}>
        11
      </div>
    );
  }
  
  return (
    <img
      src="/icons/elevenlabs.svg"
      alt="Eleven Labs"
      className={className}
      onError={() => setImgError(true)}
    />
  );
};

// Helper function to format action label for display
const formatActionLabel = (action) => {
  if (!action || action === 'N/A') return action;
  
  switch (action) {
    case 'play_recording':
      return 'Play Recording';
    case 'forward':
      return 'Forwarded';
    case 'forwardvwm':
      return 'Forwarded w/VM';
    case 'dynamic_forward':
      return 'Dynamic Forward';
    case 'send_to_vm':
      return 'Sent to VM';
    case 'ai_agent':
    case 'ai_agent':
      return 'AI Agent';
    case 'ivr':
      return 'Call Menu';
    case 'Outbound Call':
      return 'Outbound Call';
    default:
      return action;
  }
};

// Helper function to render action with icon for filter dropdown
const renderActionFilterItem = (action) => {
  if (!action || action === 'N/A') {
    return <span>{action}</span>;
  }
  
  switch (action) {
    case 'play_recording':
      return (
        <div className="flex items-center">
          <Volume2 className="w-4 h-4 text-yellow-500 mr-2" /> 
          <span>Play Recording</span>
        </div>
      );
    case 'forward':
      return (
        <div className="flex items-center">
          <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981', marginRight: '8px' }} /> 
          <span>Forwarded</span>
        </div>
      );
    case 'forwardvwm':
      return (
        <div className="flex items-center">
          <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981', marginRight: '8px' }} /> 
          <span>Forwarded w/VM</span>
        </div>
      );
    case 'dynamic_forward':
      return (
        <div className="flex items-center">
          <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981', marginRight: '8px' }} /> 
          <span>Dynamic Forward</span>
        </div>
      );
    case 'send_to_vm':
      return (
        <div className="flex items-center">
          <VoicemailOutlined sx={{ fontSize: '16px', color: '#6366F1', marginRight: '8px' }} /> 
          <span>Sent to VM</span>
        </div>
      );
    case 'ai_agent':
    case 'ai_agent':
      return (
        <div className="flex items-center">
          <ElevenLabsIcon className="w-4 h-4 mr-2" />
          <span>AI Agent</span>
        </div>
      );
    case 'ivr':
      return (
        <div className="flex items-center">
          <Settings2 className="w-4 h-4 text-indigo-500 mr-2" /> 
          <span>Call Menu</span>
        </div>
      );
    case 'Outbound Call':
      return (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-green-500 mr-2" /> 
          <span>Outbound Call</span>
        </div>
      );
    default:
      return <span>{action}</span>;
  }
};

const RecentCalls = ({ navigate, calls, onDateRangeChange, initialStartDate, initialEndDate }) => {
  const [sortConfig, setSortConfig] = useState({ key: "callTime", direction: "desc" });
  
  // Helper function to get default dates (30 days ago to today)
  const getDefaultDates = () => {
    const today = new Date();
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 30);
    return {
      startDate: pastDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  // Prefer initialStartDate/initialEndDate from props to ensure consistency with parent
  const [startDate, setStartDate] = useState(initialStartDate || defaultDates.startDate);
  const [endDate, setEndDate] = useState(initialEndDate || defaultDates.endDate);
  const [dateError, setDateError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null); // for Menu position
  const dropdownRef = useRef(null);



  //toNumber
  const [toNumberDropdownOpen, setToNumberDropdownOpen] = useState(false);
  const [toNumberAnchorEl, setToNumberAnchorEl] = useState(null);
  const [searchToNumber, setSearchToNumber] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState([]);

  const allToNumbers = Array.from(new Set(calls.map((call) => call.toNumber)));;





  // Toggle dropdown
  const toggleDropdown = (event) => {
    setAnchorEl(event.currentTarget); // Set anchor to button for Menu positioning
    setDropdownOpen((prev) => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sort handling
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
    //setDropdownOpen(false)

  };


  // Handle To Number filter
  const handleSelectAllNumbers = (checked) => {
    setSelectedNumbers(checked ? allToNumbers.filter((number) =>
      number.toLowerCase().includes(searchToNumber.toLowerCase())
    ).slice(0, 20) : []);
  };

  const handleSelectNumber = (number) => {
    setSelectedNumbers((prev) =>
      prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number]
    );
  };


  // Toggle dropdown for To Number filter
  const toggleToNumberDropdown = (event) => {
    setToNumberAnchorEl(event.currentTarget);
    setToNumberDropdownOpen((prev) => !prev);
  };



  //callNumber
  const [callNumberDropdownOpen, setCallNumberDropdownOpen] = useState(false);
  const [callNumberAnchorEl, setCallNumberAnchorEl] = useState(null);
  const [searchCallNumber, setSearchCallNumber] = useState("");
  const [selectedCallNumbers, setSelectedCallNumbers] = useState([]);

  const allCallNumbers = Array.from(new Set(calls.map((call) => call.callerNumber)));;


  // Handle To Number filter
  const handleSelectAllCallNumbers = (checked) => {
    setSelectedCallNumbers(checked ? allCallNumbers.filter((number) =>
      number.toLowerCase().includes(searchCallNumber.toLowerCase())
    ).slice(0, 20) : []);
  };

  const handleSelectCallNumber = (number) => {
    setSelectedCallNumbers((prev) =>
      prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number]
    );
  };


  // Toggle dropdown for To Number filter
  const toggleCallNumberDropdown = (event) => {
    setCallNumberAnchorEl(event.currentTarget);
    setCallNumberDropdownOpen((prev) => !prev);
  };




  //callNumber
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [searchAction, setSearchAction] = useState("");
  const [selectedActions, setSelectedActions] = useState([]);

  const allActions = Array.from(new Set(calls.map((call) => call.action))).slice(0, 20);;


  // Handle To Number filter
  const handleSelectAllActions = (checked) => {
    setSelectedActions(checked ? allActions : []);
  };

  const handleSelectAction = (number) => {
    setSelectedActions((prev) =>
      prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number]
    );
  };


  // Toggle dropdown for To Number filter
  const toggleActionDropdown = (event) => {
    setActionAnchorEl(event.currentTarget);
    setActionDropdownOpen((prev) => !prev);
  };


  //callNumber
  const [actionDetailDropdownOpen, setActionDetailDropdownOpen] = useState(false);
  const [actionDetailAnchorEl, setActionDetailAnchorEl] = useState(null);
  const [searchActionDetail, setSearchActionDetail] = useState("");
  const [selectedActionDetails, setSelectedActionDetails] = useState([]);

  const allActionDetails = Array.from(new Set(calls.map((call) => call.actionDetails))).slice(0, 20);;


  // Handle To Number filter
  const handleSelectAllActionDetails = (checked) => {
    setSelectedActionDetails(checked ? allActionDetails : []);
  };

  const handleSelectActionDetail = (number) => {
    setSelectedActionDetails((prev) =>
      prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number]
    );
  };


  // Toggle dropdown for To Number filter
  const toggleActionDetailDropdown = (event) => {
    setActionDetailAnchorEl(event.currentTarget);
    setActionDetailDropdownOpen((prev) => !prev);
  };



  //callNumber
  const [voicemailDropdownOpen, setVoicemailDropdownOpen] = useState(false);
  const [voicemailAnchorEl, setVoicemailAnchorEl] = useState(null);
  const [searchVoicemail, setSearchVoicemail] = useState("");
  const [selectedVoicemails, setSelectedVoicemails] = useState([]);

  const allVoicemails = Array.from(new Set(calls.map((call) => call.voicemail))).slice(0, 20);;


  // Handle To Number filter
  const handleSelectAllVoicemails = (checked) => {
    setSelectedVoicemails(checked ? allVoicemails : []);
  };

  const handleSelectVoicemail = (number) => {
    setSelectedVoicemails((prev) =>
      prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number]
    );
  };


  // Toggle dropdown for To Number filter
  const toggleVoicemailDropdown = (event) => {
    setVoicemailAnchorEl(event.currentTarget);
    setVoicemailDropdownOpen((prev) => !prev);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const prevStartDateRef = useRef(startDate);
  const prevEndDateRef = useRef(endDate);
  const hasInitializedRef = useRef(false);

  // Sync dates from props when they change (on mount or when parent updates)
  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      // Update refs BEFORE state to prevent triggering API call in the other useEffect
      prevStartDateRef.current = initialStartDate;
      prevEndDateRef.current = initialEndDate;
      
      // Only update state if dates are actually different
      if (startDate !== initialStartDate || endDate !== initialEndDate) {
        setStartDate(initialStartDate);
        setEndDate(initialEndDate);
      }
      
      // Mark as initialized after receiving props
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
      }
    } else if (!hasInitializedRef.current) {
      // If no initial dates provided yet, wait a bit for them to arrive
      // This prevents triggering API call before props are ready
      const timeoutId = setTimeout(() => {
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // Remove startDate and endDate from dependencies to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStartDate, initialEndDate]);

  // Trigger API call when user manually changes date range (not when syncing from props)
  useEffect(() => {
    // Skip if not yet initialized (waiting for props)
    if (!hasInitializedRef.current) {
      return;
    }

    const datesChanged = 
      startDate !== prevStartDateRef.current || endDate !== prevEndDateRef.current;

    // Only trigger if dates actually changed AND they don't match current props
    // This prevents triggering when syncing from props
    const matchesCurrentProps = 
      initialStartDate === startDate && initialEndDate === endDate;

    // Validate date range: start date must be before or equal to end date
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setDateError("Start date must be before or equal to end date");
      prevStartDateRef.current = startDate;
      prevEndDateRef.current = endDate;
      return; // Don't make API call
    } else {
      setDateError(""); // Clear error if dates are valid
    }

    if (datesChanged && !matchesCurrentProps && onDateRangeChange && startDate && endDate) {
      // Reset to first page when date range changes
      setCurrentPage(1);
      
      // Debounce to avoid too many API calls
      const timeoutId = setTimeout(() => {
        onDateRangeChange(startDate, endDate);
        prevStartDateRef.current = startDate;
        prevEndDateRef.current = endDate;
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      prevStartDateRef.current = startDate;
      prevEndDateRef.current = endDate;
    }
  }, [startDate, endDate, onDateRangeChange, initialStartDate, initialEndDate]);

  // Filter calls by other criteria (date filtering is now done server-side)
  const filteredCalls = calls.filter((call) => {
    // Only filter by non-date criteria since date filtering is done server-side
    return (
      (selectedNumbers.length === 0 || selectedNumbers.includes(call.toNumber)) &&
      (selectedCallNumbers.length === 0 || selectedCallNumbers.includes(call.callerNumber)) &&
      (selectedActions.length === 0 || selectedActions.includes(call.action)) &&
      (selectedActionDetails.length === 0 || selectedActionDetails.includes(call.actionDetails)) &&
      (selectedVoicemails.length === 0 || selectedVoicemails.includes(call.voicemail))
    );
  });


  const sortedRows = React.useMemo(() => {


    return [...filteredCalls].sort((a, b) => {
      const isAscending = sortConfig.direction === "asc";
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return isAscending ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return isAscending
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle cases where types are mixed (if applicable)
      return 0;
    });
  }, [filteredCalls, sortConfig]);


  // Pagination Logic
  const callsPerPage = 12; // Number of calls per page
  const indexOfLastCall = currentPage * callsPerPage;
  const indexOfFirstCall = indexOfLastCall - callsPerPage;
  const currentCalls = sortedRows.slice(indexOfFirstCall, indexOfLastCall);

  // Calculate total pages based on the current calls length
  let totalPages = Math.ceil(sortedRows.length / callsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    //setDropdownOpen(false);
    // setToNumberDropdownOpen(false)
    //setCallNumberDropdownOpen(false)
    //setActionDropdownOpen(false)
    // setActionDetailDropdownOpen(false)
    //setVoicemailDropdownOpen(false)
    setCurrentPage(1)
  }, [sortConfig, startDate, endDate, selectedNumbers, selectedCallNumbers, selectedActions, selectedActionDetails, selectedVoicemails]);

  const resetFilters = () => {
    const { startDate: defaultStart, endDate: defaultEnd } = getDefaultDates();
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setSelectedCallNumbers([]);
    setSelectedNumbers([]);
    setSelectedActions([]);
    setSelectedActionDetails([]);
    setSelectedVoicemails([]);

    setSortConfig({ key: "callTime", direction: "desc" });

    // Close all dropdowns
    setDropdownOpen(false);
    setToNumberDropdownOpen(false);
    setCallNumberDropdownOpen(false);
    setActionDropdownOpen(false);
    setActionDetailDropdownOpen(false);
    setVoicemailDropdownOpen(false);

    // Reset pagination
    setCurrentPage(1);
  };

  const isFilteredOrSorted = () => {
    const { startDate: defaultStart, endDate: defaultEnd } = getDefaultDates();
    return (
      startDate !== defaultStart ||
      endDate !== defaultEnd ||
      sortConfig.key !== "callTime" ||
      sortConfig.direction !== "desc" ||
      selectedCallNumbers.length >= 1 || selectedNumbers.length >= 1
      || selectedActions.length >= 1
      || selectedActionDetails.length >= 1
      || selectedVoicemails.length >= 1
    );
  };
  function timeDifferenceFromToday(dateString) {
    // Convert the input date string to a Date object
    const givenDate = new Date(dateString);

    // Get today's date
    const today = new Date();

    // Calculate the difference in milliseconds
    const timeDifference = today - givenDate;

    // Convert the time difference to days, hours, and minutes
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

    return days;
  }


  useEffect(() => {
    // Ensure the current page stays within valid bounds
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      {/* Table */}
      {/* Improved Pagination Controls using Material UI */}


      <TableContainer className="w-full overflow-x-auto lg:overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
            <h3 className="text-lg font-medium text-gray-800">Recent Calls</h3>
            <div className="flex flex-col items-end space-y-1">
              {dateError && (
                <p className="text-sm text-red-500 font-medium">{dateError}</p>
              )}
              <div className="flex space-x-2 items-center">
                {startDate && (
                  <p className="text-sm text-gray-500">Showing calls from {`${formatTimestampUtc(startDate)}`}, {timeDifferenceFromToday(startDate)} days ago.</p>
                )}
                {isFilteredOrSorted() && (
                  <Button
                    variant="contained"
                      color="primary"
                      sx={{
                      py: "4px",
                      px: "12px",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                    onClick={() => {
                      resetFilters();
                    }}
                  >
                    Reset Filters & Sort
                  </Button>
                )}
                <button 
                onClick={() => {
                  const handleExport = () => {
                    if (!sortedRows || sortedRows.length === 0) {
                      alert("No data to export");
                      return;
                    }
                    
                    // Helper function to format timestamp for Excel (YYYY-MM-DD HH:MM:SS)
                    const formatExcelTimestamp = (timestamp) => {
                      if (!timestamp || timestamp === "N/A") return "";
                      let ts = typeof timestamp === "string" ? Number(timestamp) : timestamp;
                      if (ts.toString().length === 10) {
                        ts *= 1000; // Convert seconds to milliseconds
                      }
                      const date = new Date(ts);
                      if (isNaN(date)) return "";
                      
                      // Get organization timezone
                      try {
                        const org = JSON.parse(localStorage.getItem('organization'));
                        const timeZone = org?.org_timezone?.S || 'UTC';
                        
                        // Format as Excel-friendly: YYYY-MM-DD HH:MM:SS
                        const formatter = new Intl.DateTimeFormat("en-US", {
                          timeZone,
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false
                        });
                        
                        const parts = formatter.formatToParts(date);
                        const year = parts.find(p => p.type === 'year').value;
                        const month = parts.find(p => p.type === 'month').value;
                        const day = parts.find(p => p.type === 'day').value;
                        const hour = parts.find(p => p.type === 'hour').value;
                        const minute = parts.find(p => p.type === 'minute').value;
                        const second = parts.find(p => p.type === 'second').value;
                        
                        return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
                      } catch (e) {
                        // Fallback to ISO string if timezone parsing fails
                        return date.toISOString().replace('T', ' ').substring(0, 19);
                      }
                    };
                    
                    // Define custom headers in the desired order
                    const headers = [
                      "Call Time",
                      "Call Timestamp",
                      "To Number",
                      "Caller Number",
                      "Action",
                      "Action Details",
                      "Voicemail",
                      "Call User",
                      "Link"
                    ];
                    
                    const csvRows = [
                      headers.join(","),
                      ...sortedRows.map((row) => {
                        const values = [
                          formatExcelTimestamp(row.callTime), // Call Time (Excel-friendly)
                          row.callTime || "", // Call Timestamp (raw)
                          row.toNumber || "",
                          row.callerNumber || "",
                          row.action || "",
                          row.actionDetails || "",
                          row.voicemail || "",
                          row.call_user || row.callUser || "", // Call User
                          row.link ? (window.location.origin + row.link) : "" // Full URL for Link
                        ];
                        return values.map((value) => {
                          const strValue = String(value ?? "");
                          return `"${strValue.replace(/"/g, '""')}"`;
                        }).join(",");
                      }),
                    ];
                    
                    // Generate filename with date range
                    const formatDateForFilename = (dateStr) => {
                      // dateStr is already in YYYY-MM-DD format, but ensure it's clean
                      return dateStr ? dateStr.split('T')[0] : new Date().toISOString().split('T')[0];
                    };
                    
                    const startDateFormatted = formatDateForFilename(startDate);
                    const endDateFormatted = formatDateForFilename(endDate);
                    const filename = `twilanswer_calls_${startDateFormatted}_${endDateFormatted}.csv`;
                    
                    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = filename;
                    link.click();
                    URL.revokeObjectURL(url);
                  };
                  handleExport();
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <Download size={16} className="mr-1" />
                Export
              </button>
              </div>
            </div>
          </div>
        </div>
        <Table style={{ minWidth: "1100px" }}>
          <TableHead className="bg-gray-50" sx={{ '& .MuiTableCell-head': { padding: '4px 24px' } }}>
            <TableRow>
              {/* Dropdown in Call Time */}
              <TableCell className="relative font-semibold cursor-pointer compact-header" sx={{ fontSize: '12px' }}>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center gap-1 text-[#6B7280] uppercase text-[12px] font-semibold"
                  ref={dropdownRef}
                >
                  Call Time
                  {dropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>
                <Menu
                  anchorEl={anchorEl}
                  open={dropdownOpen}
                  onClose={() => setDropdownOpen(false)}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}

                >
                  <div className="px-2">
                    <MenuItem onClick={() => handleSort('callTime', "asc")}     >
                      <div className="text-[14px] text-center w-full ">Sort Earliest to Recent</div>
                    </MenuItem>
                    <MenuItem onClick={() => handleSort('callTime', "desc")}>
                      <div className="text-[14px] text-center w-full">Sort Recent to Earliest</div>

                    </MenuItem>
                    <div className="mb-2 flex items-center space-x-3 mt-1 w-full">
                      <label className="block text-sm mb-1 w-full">Start Date</label>
                      <input
                        type="date"
                        style={{ outline: "none", backgroundColor: '#FAFAFB', border: '1px solid #FAFAFB', borderRadius: '10px' }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full border rounded px-2 py-1"
                        max={(new Date()).toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="flex items-center space-x-2" >
                      <label className="block text-sm mb-1 w-full">End Date</label>
                      <input
                        type="date"
                        style={{ outline: "none", backgroundColor: '#FAFAFB', border: '1px solid #FAFAFB', borderRadius: '10px' }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full border rounded px-2 py-1"
                        max={(new Date()).toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </Menu>
              </TableCell>
              <TableCell className="font-semibold compact-header" sx={{ fontSize: '12px' }}>
                <button
                  onClick={toggleToNumberDropdown}
                  className="flex items-center gap-1 text-[#6B7280] uppercase text-[12px] font-semibold"
                >
                  To Number
                  {toNumberDropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>
                <Menu
                  anchorEl={toNumberAnchorEl}
                  open={toNumberDropdownOpen}
                  onClose={() => setToNumberDropdownOpen(false)}
                >
                  <MenuItem onClick={() => handleSort('toNumber', "asc")}     >
                    <div className="text-[14px] text-left w-full ">Sort A-Z</div>
                  </MenuItem>
                  <MenuItem onClick={() => handleSort('toNumber', "desc")}>
                    <div className="text-[14px] text-left w-full">Sort Z-A</div>
                  </MenuItem>
                  <div className="p-2 mt-1">
                    <InputBase
                      endAdornment={
                        <Search sx={{ fontSize: '20px', color: '#C9C8D1' }} />
                      }
                      type="text"
                      placeholder="Search"
                      value={searchToNumber}
                      onChange={(e) => setSearchToNumber(e.target.value)}
                      className="w-full border rounded px-2 py-1 mb-2 outline-none bg-[rgba(250,250,251,0.3)]"
                    />
                    <MenuItem onClick={() => handleSelectAllNumbers(!(selectedNumbers.length === allToNumbers.slice(0, 20).length))}>
                      <div className="text-[14px] flex items-center">
                        <Checkbox size={'small'}
                          checked={selectedNumbers.length === allToNumbers
                            .filter((number) =>
                              number.toLowerCase().includes(searchToNumber.toLowerCase())
                            ).slice(0, 20).length}
                        // onChange={(e) => handleSelectAllNumbers(e.target.checked)}
                        />
                        Select All</div>
                    </MenuItem>
                    {allToNumbers
                      .filter((number) =>
                        number.toLowerCase().includes(searchToNumber.toLowerCase())
                      )
                      .slice(0, 20)
                      .map((number, index) => (
                        <MenuItem key={index} onClick={() => handleSelectNumber(number)}>
                          <div className="text-[14px] flex items-center">
                            <Checkbox sx={{ fontSize: '' }} size="small"
                              checked={selectedNumbers.includes(number)}
                            //onChange={() => handleSelectNumber(number)}
                            />
                            {number}
                          </div>
                        </MenuItem>
                      ))}
                  </div>
                </Menu>
              </TableCell>
              <TableCell className="font-semibold compact-header" sx={{ fontSize: '12px' }}>
                <button
                  onClick={toggleCallNumberDropdown}
                  className="flex items-center gap-1 text-[#6B7280] uppercase text-[12px] font-semibold"
                >
                  Caller Number
                  {toNumberDropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>
                <Menu
                  anchorEl={callNumberAnchorEl}
                  open={callNumberDropdownOpen}
                  onClose={() => setCallNumberDropdownOpen(false)}
                >
                  <MenuItem onClick={() => handleSort('callerNumber', "asc")}     >
                    <div className="text-[14px] text-left w-full ">Sort A-Z</div>
                  </MenuItem>
                  <MenuItem onClick={() => handleSort('callerNumber', "desc")}>
                    <div className="text-[14px] text-left w-full">Sort Z-A</div>
                  </MenuItem>
                  <div className="p-2 mt-1">
                    <InputBase
                      endAdornment={
                        <Search sx={{ fontSize: '20px', color: '#C9C8D1' }} />
                      }
                      type="text"
                      placeholder="Search"
                      value={searchCallNumber}
                      onChange={(e) => setSearchCallNumber(e.target.value)}
                      className="w-full border rounded px-2 py-1 mb-2 outline-none bg-[rgba(250,250,251,0.3)]"
                    />
                    <MenuItem onClick={() => handleSelectAllCallNumbers(!(selectedCallNumbers.length === allCallNumbers.slice(0, 20).length))}>
                      <div className="text-[14px] flex items-center">
                        <Checkbox size={'small'}
                          checked={selectedCallNumbers.length === allCallNumbers.filter((number) =>
                            number.toLowerCase().includes(searchCallNumber.toLowerCase())
                          ).slice(0, 20).length}
                        // onChange={(e) => handleSelectAllCallNumbers(e.target.checked)}
                        />
                        Select All</div>
                    </MenuItem>
                    {allCallNumbers

                      .filter((number) =>
                        number.toLowerCase().includes(searchCallNumber.toLowerCase())
                      )
                      .slice(0, 20)
                      .map((number, index) => (
                        <MenuItem key={index} onClick={() => handleSelectCallNumber(number)}>
                          <div className="text-[14px] flex items-center">
                            <Checkbox sx={{ fontSize: '' }} size="small"
                              checked={selectedCallNumbers.includes(number)}
                            //onChange={() => handleSelectCallNumber(number)}
                            />
                            {number}
                          </div>
                        </MenuItem>
                      ))}
                  </div>
                </Menu>
              </TableCell>
              <TableCell className="font-semibold compact-header" sx={{ fontSize: '12px' }}>
                <button
                  onClick={toggleActionDropdown}
                  className="flex items-center gap-1 text-[#6B7280] uppercase text-[12px] font-semibold"
                >
                  Action
                  {actionDropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>
                <Menu
                  anchorEl={actionAnchorEl}
                  open={actionDropdownOpen}
                  onClose={() => setActionDropdownOpen(false)}
                >
                  <MenuItem onClick={() => handleSort('action', "asc")}     >
                    <div className="text-[14px] text-left w-full ">Sort A-Z</div>
                  </MenuItem>
                  <MenuItem onClick={() => handleSort('action', "desc")}>
                    <div className="text-[14px] text-left w-full">Sort Z-A</div>
                  </MenuItem>
                  <div className="p-2 mt-1">
                    <InputBase
                      endAdornment={
                        <Search sx={{ fontSize: '20px', color: '#C9C8D1' }} />
                      }
                      type="text"
                      placeholder="Search"
                      value={searchAction}
                      onChange={(e) => setSearchAction(e.target.value)}
                      className="w-full border rounded px-2 py-1 mb-2 outline-none bg-[rgba(250,250,251,0.3)]"
                    />
                    <MenuItem onClick={() => handleSelectAllActions(!(selectedActions.length === allActions.length))}>
                      <div className="text-[14px] flex items-center">
                        <Checkbox size={'small'}
                          checked={selectedActions.length === allActions.length}
                        //onChange={(e) => handleSelectAllActions(e.target.checked)}
                        />
                        Select All</div>
                    </MenuItem>
                    {allActions
                      .filter((action) =>
                        formatActionLabel(action).toLowerCase().includes(searchAction.toLowerCase())
                      )
                      .map((action, index) => (
                        <MenuItem key={index} onClick={() => handleSelectAction(action)}>
                          <div className="text-[14px] flex items-center w-full">
                            <Checkbox sx={{ fontSize: '' }} size="small"
                              checked={selectedActions.includes(action)}
                            //onChange={() => handleSelectAction(action)}
                            />
                            <div className="flex items-center flex-1">
                              {renderActionFilterItem(action)}
                            </div>
                          </div>
                        </MenuItem>
                      ))}
                  </div>
                </Menu>
              </TableCell>
              <TableCell className="font-semibold">
                <button
                  onClick={toggleActionDetailDropdown}
                  className="flex items-center gap-1 text-[#6B7280] uppercase text-[12px] font-semibold"
                >
                  Action Details
                  {actionDetailDropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>
                <Menu
                  anchorEl={actionDetailAnchorEl}
                  open={actionDetailDropdownOpen}
                  onClose={() => setActionDetailDropdownOpen(false)}
                >
                  <MenuItem onClick={() => handleSort('actionDetail', "asc")}     >
                    <div className="text-[14px] text-left w-full ">Sort A-Z</div>
                  </MenuItem>
                  <MenuItem onClick={() => handleSort('actionDetail', "desc")}>
                    <div className="text-[14px] text-left w-full">Sort Z-A</div>
                  </MenuItem>
                  <div className="p-2 mt-1">
                    <InputBase
                      endAdornment={
                        <Search sx={{ fontSize: '20px', color: '#C9C8D1' }} />
                      }
                      type="text"
                      placeholder="Search"
                      value={searchActionDetail}
                      onChange={(e) => setSearchActionDetail(e.target.value)}
                      className="w-full border rounded px-2 py-1 mb-2 outline-none bg-[rgba(250,250,251,0.3)]"
                    />
                    <MenuItem onClick={() => handleSelectAllActionDetails(!(selectedActionDetails.length === allActionDetails.length))}>
                      <div className="text-[14px] flex items-center">
                        <Checkbox size={'small'}
                          checked={selectedActionDetails.length === allActionDetails.length}
                        //onChange={(e) => handleSelectAllActionDetails(e.target.checked)}
                        />
                        Select All</div>
                    </MenuItem>
                    {allActionDetails
                      .filter((actionDetail) => {
                        const formattedLabel = getActionDetailsLabel(actionDetail);
                        return formattedLabel.toLowerCase().includes(searchActionDetail.toLowerCase());
                      })
                      .map((actionDetail, index) => {
                        const formattedLabel = getActionDetailsLabel(actionDetail);
                        return (
                          <MenuItem key={index} onClick={() => handleSelectActionDetail(actionDetail)}>
                            <div className="text-[14px] flex items-center w-full">
                              <Checkbox sx={{ fontSize: '' }} size="small"
                                checked={selectedActionDetails.includes(actionDetail)}
                              //onChange={() => handleSelectActionDetail(actionDetail)}
                              />
                              <Tooltip 
                                title={actionDetail && actionDetail !== "N/A" ? actionDetail : ""}
                                arrow
                                placement="right"
                              >
                                <span className="cursor-help">{formattedLabel}</span>
                              </Tooltip>
                            </div>
                          </MenuItem>
                        );
                      })}
                  </div>
                </Menu>
              </TableCell>
              <TableCell className="font-semibold compact-header" sx={{ fontSize: '12px' }}>
                <button
                  onClick={toggleVoicemailDropdown}
                  className="flex items-center gap-1 text-[#6B7280] uppercase text-[12px] font-semibold"
                >
                  Voicemail
                  {voicemailDropdownOpen ? (
                    <ArrowDropUpIcon fontSize="small" />
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </button>
                <Menu
                  anchorEl={voicemailAnchorEl}
                  open={voicemailDropdownOpen}
                  onClose={() => setVoicemailDropdownOpen(false)}
                >
                  <MenuItem onClick={() => handleSort('voicemail', "asc")}     >
                    <div className="text-[14px] text-left w-full ">Sort A-Z</div>
                  </MenuItem>
                  <MenuItem onClick={() => handleSort('voicemail', "desc")}>
                    <div className="text-[14px] text-left w-full">Sort Z-A</div>
                  </MenuItem>
                  <div className="p-2 mt-1">
                    <InputBase
                      endAdornment={
                        <Search sx={{ fontSize: '20px', color: '#C9C8D1' }} />
                      }
                      type="text"
                      placeholder="Search"
                      value={searchVoicemail}
                      onChange={(e) => setSearchVoicemail(e.target.value)}
                      className="w-full border rounded px-2 py-1 mb-2 outline-none bg-[rgba(250,250,251,0.3)]"
                    />
                    <MenuItem onClick={() => handleSelectAllVoicemails(!(selectedVoicemails.length === allVoicemails.length))}>
                      <div className="text-[14px] flex items-center">
                        <Checkbox size={'small'}
                          checked={selectedVoicemails.length === allVoicemails.length}
                        //onChange={(e) => handleSelectAllVoicemails(e.target.checked)}
                        />
                        Select All</div>
                    </MenuItem>
                    {allVoicemails
                      .filter((number) =>
                        number.toLowerCase().includes(searchVoicemail.toLowerCase())
                      )
                      .map((data, index) => (
                        <MenuItem key={index} onClick={() => handleSelectVoicemail(data)}>
                          <div className="text-[14px] flex items-center">
                            <Checkbox sx={{ fontSize: '' }} size="small"
                              checked={selectedVoicemails.includes(data)}
                            //onChange={() => handleSelectVoicemail(data)}
                            />
                            {data}
                          </div>
                        </MenuItem>
                      ))}
                  </div>
                </Menu>
              </TableCell>
              <TableCell className="font-semibold compact-header" sx={{ fontSize: '12px', color: '#6B7280' }}>
                <span className="uppercase tracking-wider font-semibold text-[12px]">More Details</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentCalls.map((row, index) => {
              const handleRowClick = (e) => {
                // Check for modifier keys (CMD on Mac, Ctrl on PC)
                if (e.metaKey || e.ctrlKey) {
                  // Open in new tab
                  e.preventDefault();
                  const fullUrl = window.location.origin + row.link;
                  window.open(fullUrl, '_blank');
                } else {
                  // Normal navigation in same tab
                  navigate(row.link);
                }
              };

              return (
                <TableRow
                  key={index}
                  className="bg-white"
                  onClick={handleRowClick}
                  sx={{ 
                    cursor: 'pointer',
                    height: 'auto',
                    '&:hover': { backgroundColor: '#F9FAFB' },
                    borderBottom: '1px solid #E5E7EB'
                  }}
                >
                  <TableCell sx={{ fontSize: '14px', borderBottom: '1px solid #E5E7EB' }}>
                    <span className="text-sm text-gray-900">{`${formatTimestampForZone(row?.callTime)}`}</span>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', borderBottom: '1px solid #E5E7EB' }}>
                    <span className="text-sm text-gray-900">{formatPhoneNumber(row.toNumber)}</span>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', borderBottom: '1px solid #E5E7EB' }}>
                    <span className="text-sm text-gray-900">{formatPhoneNumber(row.callerNumber)}</span>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', borderBottom: '1px solid #E5E7EB' }}>
                    {(() => {
                      const action = row.action;
                      if (!action || action === 'N/A') return null;
                      
                      switch (action) {
                        case 'play_recording':
                          return (
                            <div className="flex items-center">
                              <Volume2 className="w-4 h-4 text-yellow-500" /> 
                              <span className="text-sm text-gray-900 ml-2">Play Recording</span>
                            </div>
                          );
                        case 'forward':
                          return (
                            <div className="flex items-center">
                              <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981' }} /> 
                              <span className="text-sm text-gray-900 ml-2">Forwarded</span>
                            </div>
                          );
                        case 'forwardvwm':
                          return (
                            <div className="flex items-center">
                              <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981' }} /> 
                              <span className="text-sm text-gray-900 ml-2">Forwarded w/VM</span>
                            </div>
                          );
                        case 'dynamic_forward':
                          return (
                            <div className="flex items-center">
                              <ArrowForwardIcon sx={{ fontSize: '16px', color: '#10B981' }} /> 
                              <span className="text-sm text-gray-900 ml-2">Dynamic Forward</span>
                            </div>
                          );
                        case 'send_to_vm':
                          return (
                            <div className="flex items-center">
                              <VoicemailOutlined sx={{ fontSize: '16px', color: '#6366F1' }} /> 
                              <span className="text-sm text-gray-900 ml-2">Sent to VM</span>
                            </div>
                          );
                        case 'ai_agent':
                        case 'ai_agent':
                          return (
                            <div className="flex items-center">
                              <ElevenLabsIcon className="w-4 h-4" />
                              <span className="text-sm text-gray-900 ml-2">AI Agent</span>
                            </div>
                          );
                        case 'ivr':
                          return (
                            <div className="flex items-center">
                              <Settings2 className="w-4 h-4 text-indigo-500" /> 
                              <span className="text-sm text-gray-900 ml-2">Call Menu</span>
                            </div>
                          );
                        case 'Outbound Call':
                          return (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 text-green-500" /> 
                              <span className="text-sm text-gray-900 ml-2">Outbound Call</span>
                            </div>
                          );
                        default:
                          return (
                            <div className="flex items-center">
                              <span className="text-sm text-gray-900">{action}</span>
                            </div>
                          );
                      }
                    })()}
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', borderBottom: '1px solid #E5E7EB' }}>
                    <span className="text-sm text-gray-900">{getActionDetailsLabel(row.actionDetails)}</span>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', borderBottom: '1px solid #E5E7EB' }}>
                    <span className="text-sm text-gray-900">{row.voicemail}</span>
                  </TableCell>
                  <TableCell sx={{ padding: '16px 24px', fontSize: '14px', borderBottom: '1px solid #E5E7EB' }} onClick={(e) => e.stopPropagation()}>
                    <IconButton onClick={handleRowClick}>
                      <ChevronRightIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="flex justify-between px-2 items-center py-2">
      <div className="text-[14px] text-gray-500">
      Showing {((currentPage - 1) * callsPerPage) + 1} to {Math.min(currentPage * callsPerPage, sortedRows.length)} of {sortedRows.length} results
      </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          size="small"
          siblingCount={1}
          boundaryCount={1}
        />
      </div>
    </div>
  );
};

export default RecentCalls;
